import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tab,
  Stack,
  FormControl,
  Select,
  MenuItem,
  InputLabel
} from '@mui/material';
import { TrendingUp } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { rotationAPI } from '../../services/api';

const NS = 'dashboard.sectorRotation.entryTiming';

const EntryTimingPanel = () => {
  const { t, i18n } = useTranslation();
  const language = i18n.language;
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [signals, setSignals] = useState({ valid: [], invalid: [] });
  const [activeTab, setActiveTab] = useState(0); // 0: Valid, 1: Invalid
  const [filters, setFilters] = useState({
    confidence: 'all',
    setupType: 'all'
  });

  const fetchEntryTiming = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await rotationAPI.getEntryTiming({ lane: 'rotation', limit: 50 });
      const result = response.data;
      
      if (result.success) {
        setSignals({
          valid: result.signals?.valid || [],
          invalid: result.signals?.invalid || []
        });
      } else {
        setError(result.message || t(`${NS}.errorFetch`));
      }
    } catch (err) {
      console.error('Error fetching entry timing:', err);
      setError(err.message || 'Failed to fetch entry timing signals');
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchEntryTiming();
  }, [fetchEntryTiming]);

  const getSetupTypeLabel = (setupType) => {
    const keys = {
      breakout_hh10: `${NS}.setupBreakoutHh10`,
      pullback_ema10: `${NS}.setupPullbackEma10`,
      pullback_ema20: `${NS}.setupPullbackEma20`,
      reclaim: `${NS}.setupReclaim`,
      gap: `${NS}.setupGap`
    };
    return t(keys[setupType] || setupType);
  };

  const getConfidenceLabel = (confidence) => {
    const keys = { high: `${NS}.confidenceHigh`, medium: `${NS}.confidenceMedium`, low: `${NS}.confidenceLow` };
    return t(keys[confidence] || confidence);
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('vi-VN').format(price);
  };

  const formatPercent = (value) => {
    if (value == null) return '—';
    return `${value >= 0 ? '+' : ''}${value.toFixed(2)}%`;
  };

  const renderSignalRow = (signal, isValid) => {
    const getDistanceColor = (pct) => {
      if (pct < 3) return 'success.main';
      if (pct < 5) return 'warning.main';
      return 'error.main';
    };
    
    const getATRColor = (multiple) => {
      if (multiple < 1.5) return 'success.main';
      if (multiple < 2) return 'warning.main';
      return 'error.main';
    };

    // Calculate P&L if current price exists
    const pnlPct = signal.current_price && signal.entry_price 
      ? ((signal.current_price - signal.entry_price) / signal.entry_price * 100)
      : null;

    return (
      <TableRow 
        key={`${signal.stock_code}-${signal.setup_type}`}
        sx={{ 
          bgcolor: isValid ? 'rgba(76, 175, 80, 0.04)' : 'rgba(244, 67, 54, 0.04)',
          '&:hover': { 
            bgcolor: isValid ? 'rgba(76, 175, 80, 0.08)' : 'rgba(244, 67, 54, 0.08)'
          },
          transition: 'background-color 0.2s ease',
          borderLeft: 3,
          borderLeftColor: isValid ? 'success.main' : 'error.main'
        }}
      >
        {/* Stock Info + signal_date */}
        <TableCell sx={{ py: 1.2 }}>
          <Typography variant="h6" fontWeight="700" sx={{ fontSize: '1rem', lineHeight: 1.2, mb: 0.3 }}>
            {signal.stock_code}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
            {signal.sector_name?.[language] || signal.sector_code}
            {signal.sector_rank != null && (
              <Box component="span" sx={{ ml: 0.5, fontWeight: 600, color: 'primary.main' }}>
                {t(`${NS}.sectorRank`, { rank: signal.sector_rank })}
              </Box>
            )}
          </Typography>
          {signal.signal_date && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.65rem', mt: 0.2 }}>
              {new Date(signal.signal_date).toLocaleDateString(language === 'vi' ? 'vi-VN' : 'en-GB', { day: 'numeric', month: 'short' })}
            </Typography>
          )}
        </TableCell>
        
        {/* Setup Pattern */}
        <TableCell sx={{ py: 1.2 }}>
          <Typography variant="body2" fontWeight="700" sx={{ fontSize: '0.9rem', lineHeight: 1.3 }}>
            {getSetupTypeLabel(signal.setup_type)}
          </Typography>
          <Box display="flex" alignItems="center" gap={0.8} sx={{ mt: 0.3 }}>
            <Typography 
              variant="caption" 
              sx={{ 
                fontSize: '0.75rem',
                color: signal.confidence === 'high' ? 'success.main' : signal.confidence === 'medium' ? 'warning.main' : 'text.secondary',
                fontWeight: 600
              }}
            >
              {getConfidenceLabel(signal.confidence)}
            </Typography>
            {signal.days_since_sector_entry !== null && signal.days_since_sector_entry !== undefined && (
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                • {signal.days_since_sector_entry}d
              </Typography>
            )}
          </Box>
        </TableCell>
        
        {/* Price - Horizontal Layout */}
        <TableCell sx={{ py: 1.2 }}>
          <Box display="flex" alignItems="center" gap={1.5}>
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                {t(`${NS}.colEntry`)}
              </Typography>
              <Typography variant="body2" fontWeight="700" sx={{ fontSize: '0.95rem' }}>
                {formatPrice(signal.entry_price)}
              </Typography>
            </Box>
            {signal.current_price && signal.current_price !== signal.entry_price && (
              <>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.9rem' }}>→</Typography>
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                    {t(`${NS}.colNow`)}
                  </Typography>
                  <Typography 
                    variant="body2" 
                    fontWeight="700"
                    sx={{ 
                      fontSize: '0.9rem',
                      color: pnlPct > 0 ? 'success.main' : pnlPct < 0 ? 'error.main' : 'text.primary'
                    }}
                  >
                    {formatPrice(signal.current_price)}
                    {pnlPct !== null && (
                      <Box component="span" sx={{ ml: 0.5, fontSize: '0.75rem', fontWeight: 700 }}>
                        ({pnlPct > 0 ? '+' : ''}{pnlPct.toFixed(1)}%)
                      </Box>
                    )}
                  </Typography>
                </Box>
              </>
            )}
          </Box>
        </TableCell>

        {/* EMAs - Horizontal */}
        <TableCell sx={{ py: 1.2 }}>
          <Box display="flex" alignItems="center" gap={1.5}>
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                EMA10
              </Typography>
              <Typography variant="body2" fontWeight="600" sx={{ fontSize: '0.85rem' }}>
                {formatPrice(signal.ema10)}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                EMA20
              </Typography>
              <Typography variant="body2" fontWeight="600" sx={{ fontSize: '0.85rem' }}>
                {formatPrice(signal.ema20)}
              </Typography>
            </Box>
          </Box>
        </TableCell>
        
        {/* Distance - Horizontal */}
        <TableCell sx={{ py: 1.2 }}>
          <Box display="flex" alignItems="center" gap={1.5}>
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                EMA10
              </Typography>
              <Typography 
                variant="body2" 
                fontWeight="700"
                sx={{ color: getDistanceColor(signal.distance_from_ema10_pct), fontSize: '0.9rem' }}
              >
                {formatPercent(signal.distance_from_ema10_pct)}
              </Typography>
            </Box>
            {signal.distance_from_ema20_pct !== null && signal.distance_from_ema20_pct !== undefined && (
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                  EMA20
                </Typography>
                <Typography 
                  variant="body2" 
                  fontWeight="700"
                  sx={{ color: getDistanceColor(signal.distance_from_ema20_pct), fontSize: '0.9rem' }}
                >
                  {formatPercent(signal.distance_from_ema20_pct)}
                </Typography>
              </Box>
            )}
          </Box>
        </TableCell>

        {/* ATR - Horizontal */}
        <TableCell sx={{ py: 1.2 }}>
          <Box display="flex" alignItems="center" gap={1.5}>
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                ATR
              </Typography>
              <Typography variant="body2" fontWeight="600" sx={{ fontSize: '0.85rem' }}>
                {formatPrice(signal.atr)}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                {t(`${NS}.heat`)}
              </Typography>
              <Typography 
                variant="body2" 
                fontWeight="700"
                sx={{ color: getATRColor(signal.atr_multiple), fontSize: '0.9rem' }}
              >
                {signal.atr_multiple != null ? `${signal.atr_multiple.toFixed(2)}x` : '—'}
              </Typography>
            </Box>
          </Box>
        </TableCell>
        
        {/* Status + deadlines (time_stop_deadline, fail_fast_deadline from API) */}
        <TableCell align="center" sx={{ py: 1.2 }}>
          <Typography 
            variant="body2" 
            fontWeight="700"
            sx={{ 
              color: isValid ? 'success.main' : 'error.main',
              fontSize: '0.9rem',
              mb: (signal.validation_details?.anti_chase_flags && !isValid) || signal.time_stop_deadline ? 0.3 : 0
            }}
          >
            {isValid ? t(`${NS}.statusReady`) : t(`${NS}.statusRisky`)}
          </Typography>
          {signal.validation_details?.anti_chase_flags && !isValid && (
            <Typography variant="caption" color="error.main" sx={{ display: 'block', fontSize: '0.7rem', fontWeight: 500 }}>
              {[
                signal.validation_details.anti_chase_flags.too_far_from_ema10 && 'EMA10',
                signal.validation_details.anti_chase_flags.too_far_from_ema20 && 'EMA20',
                signal.validation_details.anti_chase_flags.overheated_atr && t(`${NS}.hot`)
              ].filter(Boolean).join(', ')}
            </Typography>
          )}
          {(signal.time_stop_deadline || signal.fail_fast_deadline) && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.65rem', fontWeight: 500 }}>
              {signal.time_stop_deadline && (
                <span>{t(`${NS}.timeStop`)}: {new Date(signal.time_stop_deadline).toLocaleDateString(language === 'vi' ? 'vi-VN' : 'en-GB', { day: 'numeric', month: 'short' })}</span>
              )}
              {signal.time_stop_deadline && signal.fail_fast_deadline && ' · '}
              {signal.fail_fast_deadline && (
                <span>{t(`${NS}.failFast`)}: {new Date(signal.fail_fast_deadline).toLocaleDateString(language === 'vi' ? 'vi-VN' : 'en-GB', { day: 'numeric', month: 'short' })}</span>
              )}
            </Typography>
          )}
        </TableCell>
      </TableRow>
    );
  };

  const filterSignals = (signalsList) => {
    return signalsList.filter(signal => {
      if (filters.confidence !== 'all' && signal.confidence !== filters.confidence) {
        return false;
      }
      if (filters.setupType !== 'all' && signal.setup_type !== filters.setupType) {
        return false;
      }
      return true;
    });
  };

  // Backend returns valid/invalid limited by unique stock_code (limit_by_stock=true)
  const validSignals = filterSignals(signals.valid);
  const invalidSignals = filterSignals(signals.invalid);

  if (loading) {
    return (
      <Paper sx={{ p: 3 }}>
        <Typography>{t(`${NS}.loading`)}</Typography>
      </Paper>
    );
  }

  if (error) {
    return (
      <Paper sx={{ p: 3, borderRadius: 2, border: 1, borderColor: 'error.main' }}>
        <Typography variant="body2" color="error.main" fontWeight="600">
          {error}
        </Typography>
      </Paper>
    );
  }

  return (
    <Box>
      {/* Main Content */}
      <Paper sx={{ borderRadius: 2, overflow: 'hidden' }} elevation={0} variant="outlined">
        {/* Header - Inside Paper */}
        <Box sx={{ px: 3, pt: 2.5, pb: 2, borderBottom: 1, borderColor: 'divider' }}>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={0.5}>
            <Typography 
              variant="caption" 
              sx={{ 
                color: '#64748b', 
                fontSize: '0.7rem', 
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: 0.5,
                display: 'flex',
                alignItems: 'center',
                gap: 0.5
              }}
            >
              <TrendingUp sx={{ fontSize: 16 }} />
              {t(`${NS}.title`)}
            </Typography>
            <Typography 
              variant="caption" 
              sx={{ 
                color: '#94a3b8', 
                fontSize: '0.65rem',
                fontStyle: 'italic'
              }}
            >
              {t(`${NS}.subtitle`)}
            </Typography>
          </Box>
          <Typography 
            variant="caption" 
            sx={{ 
              color: '#64748b', 
              fontSize: '0.65rem',
              display: 'block'
            }}
          >
            {t(`${NS}.subtitlePatterns`)}
          </Typography>
        </Box>

        {/* Filters & Tabs */}
        <Box sx={{ px: 3, pt: 2, pb: 0 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
            {/* Tabs - Pure Text */}
            <Tabs 
              value={activeTab} 
              onChange={(e, v) => setActiveTab(v)}
              sx={{ 
                minHeight: 40,
                '& .MuiTab-root': { 
                  minHeight: 40,
                  textTransform: 'none',
                  fontSize: '0.9rem',
                  fontWeight: 500,
                  color: 'text.secondary',
                  '&.Mui-selected': {
                    color: 'primary.main',
                    fontWeight: 600
                  }
                },
                '& .MuiTabs-indicator': {
                  height: 3,
                  borderRadius: '3px 3px 0 0'
                }
              }}
            >
              <Tab 
                label={
                  <Box display="flex" alignItems="center" gap={1}>
                    <span>{t(`${NS}.tabReady`)}</span>
                    <Typography 
                      variant="caption" 
                      sx={{ 
                        fontWeight: 700,
                        color: 'success.main',
                        fontSize: '0.75rem'
                      }}
                    >
                      ({validSignals.length})
                    </Typography>
                  </Box>
                }
              />
              <Tab 
                label={
                  <Box display="flex" alignItems="center" gap={1}>
                    <span>{t(`${NS}.tabRisky`)}</span>
                    <Typography 
                      variant="caption" 
                      sx={{ 
                        fontWeight: 700,
                        color: 'error.main',
                        fontSize: '0.75rem'
                      }}
                    >
                      ({invalidSignals.length})
                    </Typography>
                  </Box>
                }
              />
            </Tabs>
            
            {/* Filters - Minimal */}
            <Stack direction="row" spacing={1.5}>
              <FormControl size="small" sx={{ minWidth: 130 }}>
                <InputLabel sx={{ fontSize: '0.85rem' }}>{t(`${NS}.filterConfidence`)}</InputLabel>
                <Select
                  value={filters.confidence}
                  label={t(`${NS}.filterConfidence`)}
                  onChange={(e) => setFilters({ ...filters, confidence: e.target.value })}
                  sx={{ fontSize: '0.85rem' }}
                >
                  <MenuItem value="all">{t(`${NS}.filterAll`)}</MenuItem>
                  <MenuItem value="high">{t(`${NS}.confidenceHigh`)}</MenuItem>
                  <MenuItem value="medium">{t(`${NS}.confidenceMedium`)}</MenuItem>
                  <MenuItem value="low">{t(`${NS}.confidenceLow`)}</MenuItem>
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel sx={{ fontSize: '0.85rem' }}>{t(`${NS}.filterSetupType`)}</InputLabel>
                <Select
                  value={filters.setupType}
                  label={t(`${NS}.filterSetupType`)}
                  onChange={(e) => setFilters({ ...filters, setupType: e.target.value })}
                  sx={{ fontSize: '0.85rem' }}
                >
                  <MenuItem value="all">{t(`${NS}.filterAll`)}</MenuItem>
                  <MenuItem value="breakout_hh10">{getSetupTypeLabel('breakout_hh10')}</MenuItem>
                  <MenuItem value="pullback_ema10">{getSetupTypeLabel('pullback_ema10')}</MenuItem>
                  <MenuItem value="pullback_ema20">{getSetupTypeLabel('pullback_ema20')}</MenuItem>
                  <MenuItem value="reclaim">{getSetupTypeLabel('reclaim')}</MenuItem>
                  <MenuItem value="gap">{getSetupTypeLabel('gap')}</MenuItem>
                </Select>
              </FormControl>
            </Stack>
          </Box>
        </Box>

        {/* Signals Table - Clean & Elegant */}
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.50' }}>
                <TableCell sx={{ py: 1.5, borderBottom: 2, borderColor: 'divider' }}>
                  <Typography variant="caption" fontWeight="600" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                    {t(`${NS}.colStock`)}
                  </Typography>
                </TableCell>
                <TableCell sx={{ py: 1.5, borderBottom: 2, borderColor: 'divider' }}>
                  <Typography variant="caption" fontWeight="600" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                    {t(`${NS}.colSetup`)}
                  </Typography>
                </TableCell>
                <TableCell sx={{ py: 1.5, borderBottom: 2, borderColor: 'divider' }}>
                  <Typography variant="caption" fontWeight="600" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                    {t(`${NS}.colPrice`)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.6rem', fontWeight: 400, textTransform: 'none' }}>
                    {t(`${NS}.priceCloseOnSignal`)}
                  </Typography>
                </TableCell>
                <TableCell sx={{ py: 1.5, borderBottom: 2, borderColor: 'divider' }}>
                  <Typography variant="caption" fontWeight="600" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                    EMAs
                  </Typography>
                </TableCell>
                <TableCell sx={{ py: 1.5, borderBottom: 2, borderColor: 'divider' }}>
                  <Typography variant="caption" fontWeight="600" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                    {t(`${NS}.colDistance`)}
                  </Typography>
                </TableCell>
                <TableCell sx={{ py: 1.5, borderBottom: 2, borderColor: 'divider' }}>
                  <Typography variant="caption" fontWeight="600" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                    ATR
                  </Typography>
                </TableCell>
                <TableCell align="center" sx={{ py: 1.5, borderBottom: 2, borderColor: 'divider' }}>
                  <Typography variant="caption" fontWeight="600" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                    {t(`${NS}.colStatus`)}
                  </Typography>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {activeTab === 0 ? (
                validSignals.length > 0 ? (
                  validSignals.map(signal => renderSignalRow(signal, true))
                ) : (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      <Box sx={{ py: 6 }}>
                        <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
                          {t(`${NS}.emptyReady`)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {t(`${NS}.emptyReadyHint`)}
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                )
              ) : (
                invalidSignals.length > 0 ? (
                  invalidSignals.map(signal => renderSignalRow(signal, false))
                ) : (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      <Box sx={{ py: 6 }}>
                        <Typography variant="h6" color="success.main" fontWeight="600" sx={{ mb: 1 }}>
                          {t(`${NS}.emptyRisky`)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {t(`${NS}.emptyRiskyHint`)}
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                )
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Bottom Info - Pure Text */}
        <Box sx={{ px: 3, py: 2, bgcolor: 'grey.50', borderTop: 1, borderColor: 'divider' }}>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
              {t(`${NS}.footerAntiChase`)}
            </Typography>
            
            {/* Setup Distribution - Text Only */}
            <Box display="flex" gap={2}>
              {['breakout_hh10', 'pullback_ema10', 'reclaim', 'gap'].map(setupType => {
                const count = [...validSignals, ...invalidSignals].filter(s => s.setup_type === setupType).length;
                if (count === 0) return null;
                return (
                  <Typography 
                    key={setupType} 
                    variant="caption" 
                    color="text.secondary"
                    sx={{ fontSize: '0.75rem' }}
                  >
                    {getSetupTypeLabel(setupType)}: <Box component="span" sx={{ fontWeight: 700, color: 'primary.main' }}>{count}</Box>
                  </Typography>
                );
              })}
            </Box>
          </Box>
        </Box>
      </Paper>
    </Box>
  );
};

export default EntryTimingPanel;
